import { enterpriseAPI, paymentAPI } from "..";
import { getErrorStatus } from "../utils/userFacingError";

/**
 * Client for the weekly Pix Automatico routes of enterprise-payments-api.
 *
 * Every route below is private, behind the `org_payments` RBAC chain
 * (create/view/delete/edit), and mounted under the Payments
 * base (`REACT_APP_API_PAYMENT_URL`, `/payment` behind the Ingress):
 *
 *   POST /payment/pix-automatico/enrollments              create  -> 202 enrollment
 *   GET  /payment/pix-automatico/current                  view    -> 200 enrollment | 404 not_found
 *   POST /payment/pix-automatico/enrollments/:id/cancel   delete  -> 202 enrollment (body `{}`)
 *   PUT  /payment/billing/weekly-limit                    edit    -> 200 policy
 *   GET  /payment/billing/readiness                       view    -> 200 readiness (with pixProcessingFee)
 *
 * Payments decodes every body strictly (unknown fields are refused), so the
 * request shapes here are exact. Its errors are `{"error": code}`; transport
 * failures that never reached that handler are classified by status instead.
 * No function here throws: each returns a result the caller must branch on,
 * so a failure can never be mistaken for an empty answer.
 */

/** Phases Payments reports in `state`. A phase this client does not know maps to "unknown". */
export type PixEnrollmentState =
  | "pending_provider"
  | "waiting_authorization"
  | "approved"
  | "rejected"
  | "expired"
  | "cancelled"
  | "cancel_requested"
  | "stopped"
  | "blocked"
  | "unknown";

const KNOWN_STATES: ReadonlySet<string> = new Set<PixEnrollmentState>([
  "pending_provider",
  "waiting_authorization",
  "approved",
  "rejected",
  "expired",
  "cancelled",
  "cancel_requested",
  "stopped",
  "blocked",
  "unknown",
]);

export type CancellationEligibility =
  | {
      state: "allowed";
      reason: "no_outstanding_confirmed_spend" | "already_cancel_requested" | "already_terminal";
    }
  | { state: "blocked"; reason: "confirmed_spend_outstanding" }
  | { state: "unknown"; reason: "financial_authority_unavailable" };

export const UNKNOWN_CANCELLATION_ELIGIBILITY: CancellationEligibility = {
  state: "unknown",
  reason: "financial_authority_unavailable",
};

export type PixEnrollment = {
  id: string;
  state: PixEnrollmentState;
  /** Audited operator closure of this exact rejected unknown recurrence. */
  resolution?: "operator_rejected_unknown_recurrence";
  /** Provider recurrence status (CRIADA, APROVADA, ...), empty before Efi answered. */
  authorizationStatus: string;
  /** Pix copia e cola of the recurrence QR. Present only while waiting for approval. */
  pixCopyAndPaste?: string;
  version: number;
  observedAt?: string;
  cancellationRequestedAt?: string;
  /** Missing on a legacy server: never grants permission to cancel locally. */
  cancellationEligibility?: CancellationEligibility;
};

/**
 * The Pix processing fee the company pays on each weekly Pix charge, as
 * Payments reports it in `pixProcessingFee` of GET /billing/readiness (its
 * ADR 0019 fee line, shown there since ADR 0022). The amount exists only for a
 * reviewed fee contract, the one collection preparation seals; the panel never has a
 * number of its own.
 *
 * - `reviewed`: every weekly charge carries `centsPerWeeklyCharge`.
 * - `unconfigured`: no reviewed contract is configured, so there is no amount
 *   to show, and the panel offers no authorization.
 */
export type PixProcessingFee =
  | { state: "reviewed"; centsPerWeeklyCharge: number }
  | { state: "unconfigured" };

/**
 * The company choice Payments applies to the whole weekly charge. `fixed`
 * caps services plus the processing fee (after credits); `unlimited` removes
 * that internal DaKasa cap without changing what the company actually owes.
 */
export type WeeklyLimit = { mode: "fixed"; cents: number } | { mode: "unlimited" };

/** Largest fixed amount accepted by Payments' weekly-limit contract, in cents. */
export const MAX_WEEKLY_LIMIT_CENTS = 999_999_999_999;

export type BillingReadiness = {
  enrollmentId?: string;
  authorizationStatus: string;
  authorizationReady: boolean;
  /** Omitted when no valid weekly policy is stored; the reasons say why. */
  weeklyLimit?: WeeklyLimit;
  weeklyLimitVersion: number;
  activationEnabled: boolean;
  readyForExposure: boolean;
  reasons: string[];
  observedAt?: string;
  pixProcessingFee: PixProcessingFee;
  /** Additive server minimum policy; absent on a rolling legacy API. */
  minimumNetServicesCents?: number;
  minimumChargeCents?: number;
};

export type WeeklyLimitPolicy = {
  weeklyLimit: WeeklyLimit;
  version: number;
};

/** The six codes Payments returns, plus the transport classes seen before its handler. */
export type PixAutomaticoErrorCode =
  | "activation_disabled"
  | "invalid_request"
  | "profile_incomplete"
  | "state_conflict"
  | "not_found"
  | "billing_unavailable"
  | "confirmed_spend_outstanding"
  | "unauthorized"
  | "forbidden"
  | "route_missing"
  | "rate_limited"
  | "network"
  | "malformed_response"
  | "unexpected";

const BACKEND_CODES: ReadonlySet<string> = new Set<PixAutomaticoErrorCode>([
  "activation_disabled",
  "invalid_request",
  "profile_incomplete",
  "state_conflict",
  "not_found",
  "billing_unavailable",
  "confirmed_spend_outstanding",
]);

export type PixAutomaticoFailure = {
  code: PixAutomaticoErrorCode;
  status?: number;
};

export type PixResult<T> = { ok: true; value: T } | { ok: false; failure: PixAutomaticoFailure };

function record(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

function isCount(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

function parseWeeklyLimit(data: unknown): WeeklyLimit | null {
  const body = record(data);
  if (!body) return null;
  const keys = Object.keys(body);
  if (
    body.mode === "fixed" &&
    keys.length === 2 &&
    keys.includes("mode") &&
    keys.includes("cents") &&
    isCount(body.cents) &&
    body.cents > 0 &&
    body.cents <= MAX_WEEKLY_LIMIT_CENTS
  ) {
    return { mode: "fixed", cents: body.cents };
  }
  if (body.mode === "unlimited" && keys.length === 1 && keys[0] === "mode") {
    return { mode: "unlimited" };
  }
  return null;
}

/**
 * Classifies a rejected request. Only the allowlisted Payments codes are read
 * from the body; nothing else in it is trusted or ever displayed.
 *
 * A missing `org_payments` permission does not arrive here as a 403: Payments'
 * RBAC middleware turns the non-2xx answer of Enterprise's /collaborator-rbac
 * into an error and responds 500 `{"error": true, "msg": ...}` (a boolean, not
 * a code), which reads as `billing_unavailable`. Only the payments page calls
 * these routes, and its loader already requires `org_payments` view.
 */
export function toPixAutomaticoFailure(error: unknown): PixAutomaticoFailure {
  const status = getErrorStatus(error);
  const body = record(record(record(error)?.response)?.data);
  const code = body?.error;
  if (typeof code === "string" && BACKEND_CODES.has(code)) {
    return { code: code as PixAutomaticoErrorCode, status };
  }
  if (status === undefined || status === 0) return { code: "network", status };
  if (status === 401) return { code: "unauthorized", status };
  if (status === 403) return { code: "forbidden", status };
  // A 404 without the Payments envelope is a Payments build that predates
  // these routes (or a wrong base URL), never "no enrollment".
  if (status === 404) return { code: "route_missing", status };
  if (status === 429) return { code: "rate_limited", status };
  if (status >= 500) return { code: "billing_unavailable", status };
  return { code: "unexpected", status };
}

export function parseCancellationEligibility(data: unknown): CancellationEligibility {
  const body = record(data);
  if (
    body?.state === "allowed" &&
    (body.reason === "no_outstanding_confirmed_spend" ||
      body.reason === "already_cancel_requested" ||
      body.reason === "already_terminal")
  ) {
    return { state: "allowed", reason: body.reason };
  }
  if (body?.state === "blocked" && body.reason === "confirmed_spend_outstanding") {
    return { state: "blocked", reason: body.reason };
  }
  return UNKNOWN_CANCELLATION_ELIGIBILITY;
}

export function parsePixEnrollment(data: unknown): PixEnrollment | null {
  const body = record(data);
  if (!body) return null;
  const { id, state, authorizationStatus, version } = body;
  if (typeof id !== "string" || id === "" || typeof state !== "string" || !isCount(version)) {
    return null;
  }
  if (authorizationStatus !== undefined && typeof authorizationStatus !== "string") return null;
  // A marker on an unknown or unrelated phase, or a future marker this client
  // does not understand, must never make a new authorization startable.
  const resolution =
    state === "blocked" && body.resolution === "operator_rejected_unknown_recurrence"
      ? { resolution: "operator_rejected_unknown_recurrence" as const }
      : {};
  return {
    id,
    state: (KNOWN_STATES.has(state) ? state : "unknown") as PixEnrollmentState,
    ...resolution,
    authorizationStatus: authorizationStatus ?? "",
    pixCopyAndPaste: optionalString(body.pixCopyAndPaste),
    version,
    observedAt: optionalString(body.observedAt),
    cancellationRequestedAt: optionalString(body.cancellationRequestedAt),
    cancellationEligibility: parseCancellationEligibility(body.cancellationEligibility),
  };
}

const SHA256_HEX = /^[0-9a-f]{64}$/;

/**
 * Reads `pixProcessingFee` exactly as Payments writes it. A reviewed fee needs
 * a positive whole amount and the digest of its evidence record; the other two
 * states carry neither, because Payments never sends an invented zero. Any
 * other shape, a state this client does not know or a missing field (a
 * Payments build without the fee) is refused, so the panel can never show a
 * fee Payments did not state.
 */
export function parsePixProcessingFee(data: unknown): PixProcessingFee | null {
  const body = record(data);
  if (!body) return null;
  const { state, centsPerWeeklyCharge, evidenceSha256 } = body;
  if (state === "reviewed") {
    return isCount(centsPerWeeklyCharge) &&
      centsPerWeeklyCharge > 0 &&
      typeof evidenceSha256 === "string" &&
      SHA256_HEX.test(evidenceSha256)
      ? { state: "reviewed", centsPerWeeklyCharge }
      : null;
  }
  const noAmount = centsPerWeeklyCharge === undefined && evidenceSha256 === undefined;
  if (state === "unconfigured") return noAmount ? { state: "unconfigured" } : null;
  return null;
}

export function parseBillingReadiness(data: unknown): BillingReadiness | null {
  const body = record(data);
  if (!body) return null;
  const {
    authorizationStatus,
    authorizationReady,
    weeklyLimitVersion,
    activationEnabled,
    readyForExposure,
    reasons,
  } = body;
  let weeklyLimit: WeeklyLimit | undefined;
  if (body.weeklyLimit !== undefined) {
    const parsed = parseWeeklyLimit(body.weeklyLimit);
    if (parsed === null) return null;
    weeklyLimit = parsed;
  }
  const pixProcessingFee = parsePixProcessingFee(body.pixProcessingFee);
  if (
    typeof authorizationReady !== "boolean" ||
    typeof activationEnabled !== "boolean" ||
    typeof readyForExposure !== "boolean" ||
    !isCount(weeklyLimitVersion) ||
    (weeklyLimit !== undefined && weeklyLimitVersion === 0) ||
    !Array.isArray(reasons) ||
    !reasons.every((reason) => typeof reason === "string") ||
    (authorizationStatus !== undefined && typeof authorizationStatus !== "string") ||
    pixProcessingFee === null ||
    (body.minimumNetServicesCents !== undefined && body.minimumNetServicesCents !== 100) ||
    (body.minimumChargeCents !== undefined &&
      (!isCount(body.minimumChargeCents) ||
        body.minimumNetServicesCents === undefined ||
        pixProcessingFee?.state !== "reviewed" ||
        !Number.isSafeInteger(
          body.minimumNetServicesCents + pixProcessingFee.centsPerWeeklyCharge,
        ) ||
        body.minimumChargeCents !==
          body.minimumNetServicesCents + pixProcessingFee.centsPerWeeklyCharge))
  ) {
    return null;
  }
  return {
    enrollmentId: optionalString(body.enrollmentId),
    authorizationStatus: authorizationStatus ?? "",
    authorizationReady,
    ...(weeklyLimit === undefined ? {} : { weeklyLimit }),
    weeklyLimitVersion,
    activationEnabled,
    readyForExposure,
    reasons: reasons as string[],
    observedAt: optionalString(body.observedAt),
    pixProcessingFee,
    ...(body.minimumNetServicesCents === undefined
      ? {}
      : { minimumNetServicesCents: body.minimumNetServicesCents as number }),
    ...(body.minimumChargeCents === undefined
      ? {}
      : { minimumChargeCents: body.minimumChargeCents as number }),
  };
}

export function parseWeeklyLimitPolicy(data: unknown): WeeklyLimitPolicy | null {
  const body = record(data);
  const weeklyLimit = parseWeeklyLimit(body?.weeklyLimit);
  if (!body || weeklyLimit === null || !isCount(body.version) || body.version === 0) return null;
  return { weeklyLimit, version: body.version };
}

const MALFORMED: PixAutomaticoFailure = { code: "malformed_response" };

async function request<T>(
  call: () => Promise<{ data: unknown }>,
  parse: (data: unknown) => T | null,
): Promise<PixResult<T>> {
  let data: unknown;
  try {
    data = (await call()).data;
  } catch (error) {
    return { ok: false, failure: toPixAutomaticoFailure(error) };
  }
  const value = parse(data);
  return value === null ? { ok: false, failure: MALFORMED } : { ok: true, value };
}

const headers = (jwt: string) => ({ headers: { "X-JWT": jwt } });

export function fetchBillingReadiness(jwt: string): Promise<PixResult<BillingReadiness>> {
  return request(() => paymentAPI.get("/billing/readiness", headers(jwt)), parseBillingReadiness);
}

/** `null` means Payments answered `not_found`: this organization never enrolled. */
export async function fetchCurrentEnrollment(
  jwt: string,
): Promise<PixResult<PixEnrollment | null>> {
  const result = await request(
    () => paymentAPI.get("/pix-automatico/current", headers(jwt)),
    parsePixEnrollment,
  );
  if (!result.ok && result.failure.code === "not_found") return { ok: true, value: null };
  return result;
}

export type StartEnrollmentInput = {
  idempotencyKey: string;
  termsVersion: string;
  weeklyLimit: WeeklyLimit;
};

export function startEnrollment(
  jwt: string,
  input: StartEnrollmentInput,
): Promise<PixResult<PixEnrollment>> {
  const body = {
    idempotencyKey: input.idempotencyKey,
    termsVersion: input.termsVersion,
    weeklyLimit: input.weeklyLimit,
  };
  return request(
    () => paymentAPI.post("/pix-automatico/enrollments", body, headers(jwt)),
    parsePixEnrollment,
  );
}

export function cancelEnrollment(jwt: string, id: string): Promise<PixResult<PixEnrollment>> {
  return request(
    () =>
      paymentAPI.post(
        `/pix-automatico/enrollments/${encodeURIComponent(id)}/cancel`,
        {},
        headers(jwt),
      ),
    parsePixEnrollment,
  );
}

export type UpdateWeeklyLimitInput = {
  weeklyLimit: WeeklyLimit;
  /** The version the edit was based on; 0 creates the first policy. */
  expectedVersion: number;
};

export function updateWeeklyLimit(
  jwt: string,
  input: UpdateWeeklyLimitInput,
): Promise<PixResult<WeeklyLimitPolicy>> {
  const body = { weeklyLimit: input.weeklyLimit, expectedVersion: input.expectedVersion };
  return request(
    () => paymentAPI.put("/billing/weekly-limit", body, headers(jwt)),
    parseWeeklyLimitPolicy,
  );
}

/** The answer of the Enterprise profile republication (Enterprise ADR 0009). */
export type BillingProfileSync =
  | { kind: "sent" }
  | { kind: "missing"; fields: string[] }
  | { kind: "unavailable" };

/**
 * Asks Enterprise to publish this organization's fiscal profile to Payments
 * again. Payments refuses an authorization (`profile_incomplete`) until it
 * holds one, and organizations older than that projection never sent it.
 * Enterprise fills a missing IBGE city code from the company registry and
 * answers 422 with the fields only the company can fill in.
 */
export async function syncBillingProfile(jwt: string): Promise<BillingProfileSync> {
  try {
    const { data } = await enterpriseAPI.post("/sync-billing-profile", {}, headers(jwt));
    return record(data)?.status === "sent" ? { kind: "sent" } : { kind: "unavailable" };
  } catch (error) {
    if (getErrorStatus(error) !== 422) return { kind: "unavailable" };
    const body = record(record(record(error)?.response)?.data);
    const missing: unknown[] = Array.isArray(body?.missing) ? body.missing : [];
    return {
      kind: "missing",
      fields: missing.filter((field): field is string => typeof field === "string" && field !== ""),
    };
  }
}

/**
 * RFC 4122 v4, used as the enrollment idempotency key. A retry of the same
 * start (same weekly limit) reuses its key, so an uncertain first attempt can never
 * create a second authorization.
 */
export function newIdempotencyKey(): string {
  const source = globalThis.crypto;
  if (typeof source?.randomUUID === "function") return source.randomUUID();
  if (typeof source?.getRandomValues !== "function") {
    throw new Error("A secure random source is required to start a Pix Automatico enrollment.");
  }
  const bytes = source.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Window event the payments page dispatches after something that moves the
 * organization's billing state (an approval observed, a cancellation, a
 * weekly-limit change). The shell re-reads the payment readiness on it, so the
 * sidebar follows without a reload.
 */
export const PIX_AUTOMATICO_READINESS_CHANGED_EVENT = "dakasa:pix-automatico-readiness-changed";
